import React from "react";
import ReactDOM from "react-dom";
import { useLocation, useNavigate, Link as RouterLink } from "react-router-dom";
import { clsx } from "clsx";
import { View, Text, Button, VoiceRunLogo } from "./base";
import { Icon } from "./base/Icon";
import { Link } from "./base/Link";
import { useAuth } from "../hooks/useAuth";
import { useEffectiveOrganization } from "../hooks/useEffectiveOrganization";
import { useAppSelector, useAppDispatch } from "../redux";
import { sessionActions } from "../redux/reducers/sessionSlice";
import { useTheme } from "../contexts/ThemeContext";
import { NotificationBell } from "./NotificationBell";

// Legacy types for backward compatibility (AdminLayout)
export type SidebarLink = {
  label: string;
  to: string;
};

export type SidebarSection = {
  title: string;
  links: SidebarLink[];
};

// New nav item type with category support
export type NavItem = {
  label: string;
  to?: string;
  externalUrl?: string;
  icon?: string;
  children?: NavItem[];
};

type LeftNavbarProps = {
  navItems?: NavItem[];
  sections?: SidebarSection[];
  className?: string;
  isOpen?: boolean;
  onClose?: () => void;
  isCollapsed?: boolean;
  onToggleCollapse?: () => void;
  isAdminViewingOtherOrg?: boolean;
};

// Icon lookup for nav items
const NAV_ICON_MAP: Record<string, string> = {
  Home: "home",
  Agents: "bot",
  Tooling: "construction",
  Evaluators: "checklist",
  Simulations: "play",
  Settings: "settings",
  User: "person",
  Members: "users",
  Configurations: "settings",
  Usage: "activity",
  Billing: "payments",
  Organization: "users",
  Resources: "folder_open",
  "Voice Library": "voice-over",
  "STT Lab": "mic",
  Documentation: "file",
  Admin: "admin_panel_settings",
  Users: "users",
  Organizations: "grid",
  Profile: "person",
};

const getNavIcon = (item: NavItem | { label: string }): string => {
  return ("icon" in item && item.icon) || NAV_ICON_MAP[item.label] || "folder";
};

export const LeftNavbar = ({
  navItems,
  sections = [],
  className,
  isOpen = true,
  onClose,
  isCollapsed = false,
  onToggleCollapse,
  isAdminViewingOtherOrg = false,
}: LeftNavbarProps) => {
  const { pathname } = useLocation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { organization } = useEffectiveOrganization();
  const { theme, toggleTheme } = useTheme();

  const [showOrgDropdown, setShowOrgDropdown] = React.useState(false);
  const [orgSearch, setOrgSearch] = React.useState("");
  const dispatch = useAppDispatch();
  const orgButtonRef = React.useRef<HTMLDivElement>(null);
  const sidebarRef = React.useRef<HTMLDivElement>(null);

  // Get all organizations from Redux state
  const organizations = useAppSelector((state) => state.organization.entities);
  const organizationList = Object.values(organizations).filter((org) => org && !org.deletedAt);

  const isLinkActive = (linkTo: string) => {
    if (linkTo === "/") return pathname === "/";
    if (linkTo === "/agents") {
      return pathname === linkTo || /^\/agents\/[^\/]+\/(overview|environments|function|testing|sessions)/.test(pathname);
    }
    return pathname === linkTo || pathname.startsWith(`${linkTo}/`);
  };

  // Filter organizations based on search
  const filteredOrganizations = organizationList.filter((org) => org.name.toLowerCase().includes(orgSearch.toLowerCase()));

  // Sort organizations by agent count (descending) then by name
  const sortedOrganizations = filteredOrganizations.sort((a, b) => {
    if (b.agentCount !== a.agentCount) {
      return b.agentCount - a.agentCount;
    }
    return a.name.localeCompare(b.name);
  });

  // Handle organization selection
  const handleOrgSelect = (orgId: string) => {
    setShowOrgDropdown(false);
    dispatch(sessionActions.setSelectedOrganization(orgId));
    window.location.reload();
  };

  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      const isInOrgPortal = target.closest(".org-dropdown-portal");

      if (showOrgDropdown && !target.closest(".org-dropdown-container") && !isInOrgPortal) {
        setShowOrgDropdown(false);
      }
      // Handle mobile sidebar close
      if (onClose && !target.closest(".left-navbar-container") && !target.closest(".hamburger-button")) {
        onClose();
      }
    };

    if (showOrgDropdown || (onClose && isOpen)) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [showOrgDropdown, onClose, isOpen]);

  // ── Rendering helpers ──

  const linkClasses = (isActive: boolean) =>
    clsx(
      "relative flex items-center gap-2.5 px-5 py-2 rounded-md text-[14px] font-medium transition-all duration-200",
      isActive
        ? isAdminViewingOtherOrg
          ? "bg-cyan-200/80 dark:bg-cyan-800/50 text-gray-900 dark:text-white before:absolute before:left-0 before:top-0 before:bottom-0 before:w-0.5 before:bg-cyan-500 dark:before:bg-cyan-400 before:rounded-r"
          : "bg-gray-200 dark:bg-[#0c1422] text-gray-900 dark:text-white before:absolute before:left-0 before:top-0 before:bottom-0 before:w-0.5 before:bg-brand dark:before:bg-cyan-400 before:rounded-r"
        : isAdminViewingOtherOrg
          ? "text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-cyan-100/50 dark:hover:bg-cyan-900/30"
          : "text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-[#0a0a0a]/50",
    );

  const childLinkClasses = (isActive: boolean) =>
    clsx(
      "relative flex items-center gap-2.5 px-4 py-1.5 rounded-md text-[14px] font-medium transition-all duration-200",
      isActive
        ? isAdminViewingOtherOrg
          ? "bg-cyan-200/80 dark:bg-cyan-800/50 text-gray-900 dark:text-white before:absolute before:left-0 before:top-0 before:bottom-0 before:w-0.5 before:bg-cyan-500 dark:before:bg-cyan-400 before:rounded-r"
          : "bg-gray-200 dark:bg-[#0c1422] text-gray-900 dark:text-white before:absolute before:left-0 before:top-0 before:bottom-0 before:w-0.5 before:bg-brand dark:before:bg-cyan-400 before:rounded-r"
        : isAdminViewingOtherOrg
          ? "text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-cyan-100/50 dark:hover:bg-cyan-900/30"
          : "text-gray-500 dark:text-gray-500 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-[#0a0a0a]/50",
    );

  const collapsedLinkClasses = (isActive: boolean) =>
    clsx(
      "relative flex items-center justify-center py-2.5 rounded-md transition-all duration-200",
      isActive
        ? isAdminViewingOtherOrg
          ? "bg-cyan-200/80 dark:bg-cyan-800/50 text-gray-900 dark:text-white before:absolute before:left-0 before:top-0 before:bottom-0 before:w-0.5 before:bg-cyan-500 dark:before:bg-cyan-400 before:rounded-r"
          : "bg-gray-200 dark:bg-[#0c1422] text-gray-900 dark:text-white before:absolute before:left-0 before:top-0 before:bottom-0 before:w-0.5 before:bg-brand dark:before:bg-cyan-400 before:rounded-r"
        : isAdminViewingOtherOrg
          ? "text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-cyan-100/50 dark:hover:bg-cyan-900/30"
          : "text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-[#0a0a0a]/50",
    );

  // Helper: render a nav link (internal or external)
  const renderLink = (item: NavItem, className: string, children: React.ReactNode, key?: string) => {
    if (item.externalUrl) {
      return (
        <a
          key={key || item.label}
          href={item.externalUrl}
          target="_blank"
          rel="noopener noreferrer"
          className={className}
        >
          {children}
        </a>
      );
    }
    // Use RouterLink directly so the custom Link component's baked-in
    // color classes don't conflict with the nav's own style system.
    return (
      <RouterLink
        key={key || item.label}
        to={item.to || "/"}
        onClick={() => onClose && onClose()}
        className={className}
      >
        {children}
      </RouterLink>
    );
  };

  // ── Render: new navItems mode ──

  const renderNavItems = () => {
    if (!navItems) return null;

    if (isCollapsed) {
      // Collapsed: show icon links, preserve gaps where category headers are
      return (
        <View className="space-y-1">
          {navItems.map((item) => {
            if (!item.children) {
              // Top-level link
              const isActive = item.to ? isLinkActive(item.to) : false;
              return (
                <div
                  key={item.label}
                  title={item.label}
                >
                  {renderLink(
                    item,
                    collapsedLinkClasses(isActive),
                    <Icon
                      name={getNavIcon(item)}
                      size={16}
                    />,
                  )}
                </div>
              );
            }

            // Category: spacer gap + child icon links
            return (
              <div key={item.label}>
                {/* Spacer matching the category header gap */}
                <div className="pt-3" />
                {item.children.map((child) => {
                  const childActive = child.to ? isLinkActive(child.to) : false;
                  return (
                    <div
                      key={child.label}
                      title={child.label}
                    >
                      {renderLink(
                        child,
                        collapsedLinkClasses(childActive),
                        <Icon
                          name={getNavIcon(child)}
                          size={16}
                        />,
                      )}
                    </div>
                  );
                })}
              </div>
            );
          })}
        </View>
      );
    }

    // Expanded: icons + labels, muted category headers, children always visible
    return (
      <View className="space-y-1">
        {navItems.map((item) => {
          // Top-level link (no children)
          if (!item.children) {
            const isActive = item.to ? isLinkActive(item.to) : false;
            return renderLink(
              item,
              linkClasses(isActive),
              <>
                <Icon
                  name={getNavIcon(item)}
                  size={16}
                  className="flex-shrink-0"
                />
                <span>{item.label}</span>
              </>,
            );
          }

          // Category: muted header + always-visible children with icons
          return (
            <div key={item.label}>
              <Text
                className={clsx(
                  "px-5 pt-4 pb-1 text-[11px] font-semibold tracking-widest uppercase",
                  isAdminViewingOtherOrg ? "text-cyan-400/70 dark:text-cyan-600/70" : "text-gray-400 dark:text-gray-500",
                )}
              >
                {item.label}
              </Text>
              <div className="pl-3 space-y-0.5">
                {item.children.map((child) => {
                  const childActive = child.to ? isLinkActive(child.to) : false;
                  return renderLink(
                    child,
                    childLinkClasses(childActive),
                    <>
                      <Icon
                        name={getNavIcon(child)}
                        size={16}
                        className="flex-shrink-0"
                      />
                      <span>{child.label}</span>
                    </>,
                  );
                })}
              </div>
            </div>
          );
        })}
      </View>
    );
  };

  const renderProfileShortcut = () => {
    return (
      <Button
        type="button"
        variant="ghost"
        onClick={() => {
          navigate("/profile");
          onClose && onClose();
        }}
        title="User"
        className={clsx(
          "!p-0 !w-full rounded-md transition-colors flex justify-center",
          isAdminViewingOtherOrg
            ? "bg-cyan-50 dark:bg-cyan-900/40 border border-cyan-200 dark:border-cyan-700 hover:bg-cyan-100 dark:hover:bg-cyan-800/50"
            : "bg-white dark:bg-[#0c1422] border border-gray-200 dark:border-[#1a2535] hover:bg-gray-50 dark:hover:bg-[#0a0a0a]/80",
        )}
      >
        <Icon
          name="person"
          size={20}
          className={isAdminViewingOtherOrg ? "text-cyan-600 dark:text-cyan-400" : "text-gray-500 dark:text-gray-500"}
        />
      </Button>
    );
  };

  // ── Render: legacy sections mode (for AdminLayout) ──

  const renderSections = () => {
    if (navItems) return null;

    return sections.map((section, sectionIdx) => (
      <View
        key={section.title || sectionIdx}
        className={clsx(sectionIdx > 0 && section.title && "mt-8")}
      >
        {sectionIdx > 0 && !section.title && sections[sectionIdx - 1]?.title && <View className="mx-3 my-4 border-t border-gray-200 dark:border-[#1a2535]" />}
        {!isCollapsed && section.title && (
          <Text className="px-3 mb-3 text-[10px] font-semibold text-gray-500 dark:text-gray-500 tracking-widest uppercase">{section.title}</Text>
        )}
        <View className="space-y-1">
          {section.links.map((link) => {
            const isActive = isLinkActive(link.to);
            return isCollapsed ? (
              <div
                key={link.to}
                title={link.label}
              >
                <Link
                  to={link.to}
                  onClick={() => onClose && onClose()}
                  className={collapsedLinkClasses(isActive)}
                >
                  <Icon
                    name={getNavIcon({ label: link.label })}
                    size={20}
                  />
                </Link>
              </div>
            ) : (
              <Link
                key={link.to}
                to={link.to}
                onClick={() => onClose && onClose()}
                className={linkClasses(isActive)}
              >
                {link.label}
              </Link>
            );
          })}
        </View>
      </View>
    ));
  };

  return (
    <>
      {/* Mobile backdrop */}
      {onClose && isOpen && <View className="fixed inset-0 bg-black/50 z-40 lg:hidden" />}

      <View
        ref={sidebarRef}
        className={clsx(
          "h-screen border-r flex flex-col left-navbar-container transition-all duration-300",
          isAdminViewingOtherOrg
            ? "bg-gradient-to-b from-cyan-50 via-teal-50 to-cyan-100 dark:from-cyan-950 dark:via-teal-950 dark:to-cyan-900 border-cyan-200 dark:border-cyan-800"
            : "bg-gray-50 dark:bg-[#0a0f1a] border-gray-200 dark:border-[#1a2535]",
          isCollapsed ? "w-[60px]" : "w-[220px]",
          onClose ? "fixed top-0 left-0 z-50 lg:fixed lg:top-0 lg:left-0 lg:z-auto" : "fixed top-0 left-0",
          onClose && !isOpen && "-translate-x-full lg:translate-x-0",
          onClose && isOpen && "translate-x-0",
          className,
        )}
      >
        {/* Collapse toggle button */}
        {onToggleCollapse && (
          <button
            onClick={onToggleCollapse}
            className="absolute top-7 -right-3.5 items-center justify-center w-7 h-7 rounded-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors z-50 hidden lg:flex"
          >
            <Icon
              name={isCollapsed ? "chevron-right" : "chevron-left"}
              size={12}
              className="text-gray-600 dark:text-gray-400"
            />
          </button>
        )}

        <View className={clsx(isCollapsed ? "px-4" : "px-6", isAdminViewingOtherOrg ? "pt-3 pb-5" : "py-7")}>
          {/* Mobile close button */}
          {onClose && !isCollapsed && (
            <button
              onClick={onClose}
              className="absolute top-6 right-6 lg:hidden p-1 rounded-md hover:bg-gray-200 dark:hover:bg-gray-800 transition-colors"
            >
              <Icon
                name="close"
                size={20}
                className="text-gray-600 dark:text-gray-400"
              />
            </button>
          )}

          {/* Admin mode badge */}
          {isAdminViewingOtherOrg && (
            <View className={clsx("mb-3 flex", isCollapsed ? "justify-center" : "justify-start")}>
              <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-bold uppercase tracking-wider bg-cyan-500 dark:bg-cyan-600 text-white">
                <Icon
                  name="admin_panel_settings"
                  size={12}
                />
                {isCollapsed ? "Admin" : "Admin Mode"}
              </span>
            </View>
          )}

          <Link
            to="/"
            className="flex items-center gap-2 justify-start"
            onClick={() => onClose && onClose()}
          >
            <div className="flex items-center justify-center w-7 h-7">
              <img
                src="/images/logos/logo.png"
                alt="VoiceRun"
                className="w-full h-full object-contain"
              />
            </div>
            {!isCollapsed && (
              <span
                className="items-center hidden sm:flex text-2xl font-bold text-slate-900 dark:text-white whitespace-nowrap"
                style={{ fontFamily: "'Rajdhani', sans-serif" }}
              >
                VoiceRun
              </span>
            )}
          </Link>
        </View>

        {/* Navigation items */}
        <View className={clsx("flex-1 overflow-y-auto", isCollapsed ? "px-2" : "px-3")}>
          {renderNavItems()}
          {renderSections()}
        </View>

        {/* Bottom utilities: Theme toggle + Notifications */}
        <View
          className={clsx(
            "relative border-t",
            isAdminViewingOtherOrg
              ? "border-cyan-200 dark:border-cyan-800 bg-cyan-100/50 dark:bg-cyan-900/30"
              : "border-gray-200 dark:border-[#1a2535] bg-gray-50 dark:bg-[#0a0f1a]",
            isCollapsed ? "p-2" : "p-3",
          )}
        >
          <View className={clsx("flex", isCollapsed ? "flex-col items-center gap-2" : "items-center justify-evenly gap-2")}>
            {/* User/Profile */}
            {renderProfileShortcut()}

            {/* Theme Toggle */}
            <Button
              type="button"
              variant="ghost"
              onClick={toggleTheme}
              title={theme === "light" ? "Switch to dark mode" : "Switch to light mode"}
              className={clsx(
                "!p-0 !w-full rounded-md transition-colors flex justify-center",
                isAdminViewingOtherOrg
                  ? "bg-cyan-50 dark:bg-cyan-900/40 border border-cyan-200 dark:border-cyan-700 hover:bg-cyan-100 dark:hover:bg-cyan-800/50"
                  : "bg-white dark:bg-[#0c1422] border border-gray-200 dark:border-[#1a2535] hover:bg-gray-50 dark:hover:bg-[#0a0a0a]/80",
              )}
            >
              <Icon
                name={theme === "light" ? "moon" : "sun"}
                size={20}
                className={isAdminViewingOtherOrg ? "text-cyan-600 dark:text-cyan-400" : "text-gray-500 dark:text-gray-500"}
              />
            </Button>

            {/* Notification Bell - admin only */}
            {user?.isAdmin && (
              <NotificationBell
                variant="outlined"
                fullWidth={isCollapsed}
                isAdminViewingOtherOrg={isAdminViewingOtherOrg}
              />
            )}
          </View>

          {/* Organization Changer - Only show for admin users */}
          {user?.isAdmin && (
            <View className="mt-3 org-dropdown-container">
              <View className="relative">
                <div
                  ref={orgButtonRef}
                  title={isCollapsed ? "Organization" : undefined}
                >
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={() => setShowOrgDropdown(!showOrgDropdown)}
                    className={clsx(
                      "w-full rounded-md transition-colors flex items-center",
                      isAdminViewingOtherOrg
                        ? "bg-cyan-50 dark:bg-cyan-900/40 border border-cyan-200 dark:border-cyan-700 hover:bg-cyan-100 dark:hover:bg-cyan-800/50"
                        : "bg-white dark:bg-[#0c1422] border border-gray-200 dark:border-[#1a2535] hover:bg-gray-50 dark:hover:bg-[#0a0a0a]/80",
                      isCollapsed ? "p-2 justify-start" : "pl-2 pr-3 py-2.5 justify-between",
                    )}
                  >
                    {isCollapsed ? (
                      <Icon
                        name="grid"
                        size={20}
                        className={isAdminViewingOtherOrg ? "text-cyan-600 dark:text-cyan-400" : "text-gray-500 dark:text-gray-500"}
                      />
                    ) : (
                      <>
                        <View className="flex items-center gap-2 min-w-0">
                          <Icon
                            name="grid"
                            size={16}
                            className={clsx("flex-shrink-0", isAdminViewingOtherOrg ? "text-cyan-600 dark:text-cyan-400" : "text-gray-500 dark:text-gray-500")}
                          />
                          <Text className="text-sm font-medium text-gray-900 dark:text-white text-left flex-1 whitespace-normal break-words line-clamp-2">
                            {organization?.name || "Select Organization"}
                          </Text>
                        </View>
                        <Icon
                          name={showOrgDropdown ? "chevron-up" : "chevron-down"}
                          size={16}
                          className={clsx("flex-shrink-0", isAdminViewingOtherOrg ? "text-cyan-600 dark:text-cyan-400" : "text-gray-500 dark:text-gray-500")}
                        />
                      </>
                    )}
                  </Button>
                </div>

                {showOrgDropdown && !isCollapsed && (
                  <View className="absolute bottom-full left-0 right-0 mb-1 bg-white dark:bg-[#0c1422] border border-gray-200 dark:border-[#1a2535] rounded-md shadow-lg overflow-hidden z-50">
                    <View className="p-3">
                      <input
                        type="text"
                        className="w-full p-2 border border-gray-200 dark:border-[#1a2535] rounded text-gray-900 dark:text-white bg-gray-50 dark:bg-[#1a2535] placeholder-gray-400 dark:placeholder-[#555] text-sm"
                        placeholder="Search organizations..."
                        value={orgSearch}
                        onChange={(e) => setOrgSearch(e.target.value)}
                        autoFocus
                      />
                    </View>
                    <View className="max-h-48 overflow-y-auto">
                      {sortedOrganizations.map((org) => (
                        <button
                          key={org.id}
                          onClick={() => handleOrgSelect(org.id)}
                          className="w-full text-left px-4 py-2.5 text-sm font-medium text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-50 dark:hover:bg-[#1a1a1a] transition-colors flex items-center justify-between"
                        >
                          <span>{org.name}</span>
                        </button>
                      ))}
                      {sortedOrganizations.length === 0 && <View className="px-4 py-2.5 text-sm text-gray-500 dark:text-gray-500">No organizations found</View>}
                    </View>
                  </View>
                )}
              </View>
            </View>
          )}
        </View>
      </View>

      {/* Portal: org dropdown when collapsed */}
      {showOrgDropdown &&
        isCollapsed &&
        orgButtonRef.current &&
        ReactDOM.createPortal(
          <View
            className="org-dropdown-portal fixed bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg overflow-hidden z-[10000] w-64"
            style={{
              top: orgButtonRef.current.getBoundingClientRect().top - 200,
              left: (sidebarRef.current?.getBoundingClientRect().left ?? 0) + 12,
            }}
          >
            <View className="p-3">
              <input
                type="text"
                className="w-full p-2 border border-gray-200 dark:border-gray-700 rounded text-gray-900 dark:text-white bg-gray-50 dark:bg-gray-800 placeholder-gray-400 dark:placeholder-gray-500 text-sm"
                placeholder="Search organizations..."
                value={orgSearch}
                onChange={(e) => setOrgSearch(e.target.value)}
                autoFocus
              />
            </View>
            <View className="max-h-48 overflow-y-auto">
              {sortedOrganizations.map((org) => (
                <button
                  key={org.id}
                  onClick={() => handleOrgSelect(org.id)}
                  className="w-full text-left px-4 py-2.5 text-sm font-medium text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors flex items-center justify-between"
                >
                  <span>{org.name}</span>
                </button>
              ))}
              {sortedOrganizations.length === 0 && <View className="px-4 py-2.5 text-sm text-gray-500 dark:text-gray-400">No organizations found</View>}
            </View>
          </View>,
          document.body,
        )}
    </>
  );
};
